import { supabase } from '../main.js';
import { icon, stars } from '../utils/icons.js';
import { renderFooter } from '../components/Footer.js'
import { projects } from '../content/work.js';

// Order of the hero showcase. Lead with the most visual work.
const FEATURED = ['plush-club', 'halo-ce', 'hoa-right', 'notchdeck', 'shotscraft', 'dungindragin', 'called-or-not', 'bricked-up', 'fitnitapp', 'byheadless'];

function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function hostOf(url) {
  try { return new URL(url).host.replace(/^www\./, ''); } catch { return ''; }
}

function renderWorkRail() {
  const bySlug = new Map(projects.map(p => [p.slug, p]));
  return FEATURED.map(slug => bySlug.get(slug)).filter(Boolean).map((p, i) => {
    const host = hostOf(p.demo_url);
    return `
      <li class="hero-work-item" style="--i:${i}">
        <a class="hero-work-card" href="/projects/${esc(p.slug)}" aria-label="${esc(p.title)} case study">
          <div class="hero-work-window">
            <div class="hero-work-bar">${icon('lock', { size: 11 })}<span class="hero-work-url">${esc(host)}</span></div>
            <img class="hero-work-shot" src="${esc(p.image_url)}" alt="" width="1600" height="1000" loading="${i < 4 ? 'eager' : 'lazy'}" decoding="async">
          </div>
          <p class="hero-work-caption"><span class="hero-work-name">${esc(p.title)}</span><span class="hero-work-kind">${esc(p.category)}</span></p>
        </a>
        ${host ? `<a class="hero-work-live" href="${esc(p.demo_url)}" target="_blank" rel="noopener noreferrer" aria-label="Visit ${esc(p.title)} at ${esc(host)}">Visit ${icon('arrow-up-right', { size: 11 })}</a>` : ''}
      </li>`;
  }).join('');
}

export async function renderHomePage(container) {
  container.innerHTML = `
    <!-- Hero: pitch, then the work itself above the fold -->
    <section class="hero-search">
      <div class="rm-container">
        <div class="hero-search-inner">
          <h1 class="hero-search-title">From Problem<br>to Solution</h1>
          <p class="hero-search-sub">Tell me what you need built. I'll handle the rest.</p>
          <div class="hero-search-bar">
            <span class="hero-search-icon">${icon('search', { size: 18 })}</span>
            <div class="search-prompts">
              <span class="search-prompt-text"></span><span class="search-cursor">|</span>
            </div>
            <a href="https://cal.com/cadenburleson" target="_blank" rel="noopener noreferrer" class="rm-btn rm-btn-primary hero-search-cta">
              Book a Call
            </a>
          </div>
        </div>
      </div>

      <div class="hero-work" role="region" aria-labelledby="hero-work-title">
        <div class="rm-container hero-work-head">
          <h2 class="hero-work-title" id="hero-work-title">Recently shipped</h2>
          <div class="hero-work-actions">
            <a href="/projects" class="hero-work-all">All case studies</a>
            <button type="button" class="hero-work-nav" data-dir="-1" aria-label="Previous projects">${icon('arrow-left', { size: 16 })}</button>
            <button type="button" class="hero-work-nav" data-dir="1" aria-label="Next projects">${icon('arrow-right', { size: 16 })}</button>
          </div>
        </div>
        <ul class="hero-work-rail">${renderWorkRail()}</ul>
      </div>

      <div class="rm-container hero-stats-wrap">
        <div>
          <div class="hero-stats-row">
            <div class="hero-stat">
              <span class="hero-stat-num">50+</span>
              <span class="hero-stat-label">Projects shipped</span>
            </div>
            <div class="hero-stat-divider"></div>
            <div class="hero-stat">
              <span class="hero-stat-num">48hr</span>
              <span class="hero-stat-label">Avg. delivery</span>
            </div>
            <div class="hero-stat-divider"></div>
            <div class="hero-stat">
              <span class="hero-stat-num">100%</span>
              <span class="hero-stat-label">Client satisfaction</span>
            </div>
            <div class="hero-stat-divider"></div>
            <div class="hero-stat">
              <span class="hero-stat-num">$1,999</span>
              <span class="hero-stat-label">Flat monthly rate</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Capabilities Bento -->
    <section class="rm-bento">
      <div class="rm-container">
        <div class="rm-bento-header">
          <p class="rm-eyebrow">What's Included</p>
          <h2 class="rm-section-title">Everything you need to win</h2>
          <p class="rm-section-description">End-to-end product development that covers every detail.</p>
        </div>
        <div class="bento-grid">
          <div class="bento-card bento-6 bento-row-2 bento-solution">
            <p class="rm-eyebrow">Process Preview</p>
            <h3>See your product come to life</h3>
            <p>Wireframes to pixel-perfect prototypes. Interaction, animation, and detail crafted for impact.</p>
            <ul class="bento-solution-list">
              <li><span class="check-icon">${icon('check', { size: 14 })}</span> Interactive prototypes you can click through</li>
              <li><span class="check-icon">${icon('check', { size: 14 })}</span> Real-time collaboration and feedback</li>
              <li><span class="check-icon">${icon('check', { size: 14 })}</span> Developer handoff with design specs</li>
              <li><span class="check-icon">${icon('check', { size: 14 })}</span> Unlimited revisions until it's perfect</li>
            </ul>
          </div>

          <div class="bento-card bento-3 bento-mini">
            <div class="bento-icon">${icon('target', { size: 18 })}</div>
            <h3>Strategic Design</h3>
            <p>Research and market analysis that uncover the insights competitors miss.</p>
          </div>
          <div class="bento-card bento-3 bento-mini">
            <div class="bento-icon">${icon('zap', { size: 18 })}</div>
            <h3>Fast Development</h3>
            <p>Modern stack. Clean code. Shipped on time, every time.</p>
          </div>
          <div class="bento-card bento-3 bento-mini">
            <div class="bento-icon">${icon('palette', { size: 18 })}</div>
            <h3>Design Systems</h3>
            <p>Scalable component libraries that make your team move 10x faster.</p>
          </div>
          <div class="bento-card bento-3 bento-mini">
            <div class="bento-icon">${icon('lock', { size: 18 })}</div>
            <h3>Production Ready</h3>
            <p>Security, accessibility, and performance optimized out of the box.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Outcomes Bento -->
    <section class="rm-bento rm-bento--secondary">
      <div class="rm-container">
        <div class="rm-bento-header">
          <p class="rm-eyebrow">Why Partner With Me</p>
          <h2 class="rm-section-title">Outcomes you can measure</h2>
        </div>
        <div class="bento-grid">
          <div class="bento-card bento-3 bento-mini">
            <div class="bento-icon">${icon('dollar-sign', { size: 18 })}</div>
            <h3>Save Money</h3>
            <p>Senior-level design and development for the cost of one hire.</p>
          </div>
          <div class="bento-card bento-3 bento-mini">
            <div class="bento-icon">${icon('clock', { size: 18 })}</div>
            <h3>Ship Faster</h3>
            <p>Skip the learning curve. Get to market 3x faster.</p>
          </div>
          <div class="bento-card bento-3 bento-mini">
            <div class="bento-icon">${icon('target', { size: 18 })}</div>
            <h3>Reduce Risk</h3>
            <p>Validate ideas with prototypes before you commit.</p>
          </div>
          <div class="bento-card bento-3 bento-mini">
            <div class="bento-icon">${icon('trending-up', { size: 18 })}</div>
            <h3>Increase Conversions</h3>
            <p>Every pixel designed to convert. Lower bounce, better metrics.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Bento -->
    <section class="rm-bento">
      <div class="rm-container">
        <div class="rm-bento-header">
          <p class="rm-eyebrow">Pricing</p>
          <h2 class="rm-section-title">Simple, transparent pricing</h2>
          <p class="rm-section-description">One flat monthly rate. Pause or cancel anytime.</p>
        </div>
        <div class="bento-grid">
          <div class="bento-card bento-7 bento-pricing-main">
            <div class="pricing-header">
              <h3 class="pricing-plan-name">Monthly Subscription</h3>
              <div class="pricing-amount">
                <span class="pricing-currency">$</span>
                <span class="pricing-price">1,999</span>
                <span class="pricing-period">/month</span>
              </div>
            </div>
            <div class="pricing-features">
              <div class="pricing-feature">
                <span class="pricing-feature-icon">${icon('check', { size: 12 })}</span>
                <span class="pricing-feature-text">One request at a time</span>
              </div>
              <div class="pricing-feature">
                <span class="pricing-feature-icon">${icon('check', { size: 12 })}</span>
                <span class="pricing-feature-text">Avg. 48-hour delivery</span>
              </div>
              <div class="pricing-feature">
                <span class="pricing-feature-icon">${icon('check', { size: 12 })}</span>
                <span class="pricing-feature-text">Unlimited monthly requests</span>
              </div>
              <div class="pricing-feature">
                <span class="pricing-feature-icon">${icon('check', { size: 12 })}</span>
                <span class="pricing-feature-text">Pause or cancel anytime</span>
              </div>
            </div>
            <a href="https://cal.com/cadenburleson" target="_blank" rel="noopener noreferrer" class="rm-btn rm-btn-primary pricing-cta">
              Get Started
              <span class="btn-icon">${icon('arrow-right', { size: 16 })}</span>
            </a>
          </div>
          <div class="bento-quote-stack">
            <div class="bento-card">
              <span class="quote-icon">${icon('dollar-sign', { size: 18 })}</span>
              <p class="quote-text">Half the price of a senior developer — without the overhead.</p>
            </div>
            <div class="bento-card">
              <span class="quote-icon">${icon('unlock-keyhole', { size: 18 })}</span>
              <p class="quote-text">No contracts. No lock-in. Cancel anytime with zero hassle.</p>
            </div>
            <div class="bento-card">
              <span class="quote-icon">${icon('zap', { size: 18 })}</span>
              <p class="quote-text">Scale up or pause when you need. Total flexibility.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Process + Tech Bento -->
    <section class="rm-bento rm-bento--secondary">
      <div class="rm-container">
        <div class="rm-bento-header">
          <p class="rm-eyebrow">Process</p>
          <h2 class="rm-section-title">How it works</h2>
        </div>
        <div class="bento-grid">
          <div class="bento-card bento-3 bento-step" data-step="1">
            <div class="step-row">
              <span class="step-num">Step 01</span>
              <span class="step-icon">${icon('phone', { size: 20 })}</span>
            </div>
            <h3>Discovery Call</h3>
            <p>We discuss your goals, timeline, and vision.</p>
          </div>
          <div class="bento-card bento-3 bento-step" data-step="2">
            <div class="step-row">
              <span class="step-num">Step 02</span>
              <span class="step-icon">${icon('search', { size: 20 })}</span>
            </div>
            <h3>Research &amp; Strategy</h3>
            <p>Deep dive into users, competitors, and market.</p>
          </div>
          <div class="bento-card bento-3 bento-step" data-step="3">
            <div class="step-row">
              <span class="step-num">Step 03</span>
              <span class="step-icon">${icon('settings', { size: 20 })}</span>
            </div>
            <h3>Design &amp; Build</h3>
            <p>Rapid prototyping, weekly check-ins, and feedback.</p>
          </div>
          <div class="bento-card bento-3 bento-step" data-step="4">
            <div class="step-row">
              <span class="step-num">Step 04</span>
              <span class="step-icon">${icon('rocket', { size: 20 })}</span>
            </div>
            <h3>Launch &amp; Support</h3>
            <p>Deployment, training, and post-launch optimization.</p>
          </div>

          <div class="bento-tech-row">
            <p class="rm-eyebrow">Stack</p>
            <span class="tech-pill">React</span>
            <span class="tech-pill">TypeScript</span>
            <span class="tech-pill">Next.js</span>
            <span class="tech-pill">React Native</span>
            <span class="tech-pill">Tailwind</span>
            <span class="tech-pill">Figma</span>
            <span class="tech-pill">Supabase</span>
            <span class="tech-pill">Vite</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Bento -->
    <section class="rm-bento">
      <div class="rm-container">
        <div class="rm-bento-header">
          <p class="rm-eyebrow">Testimonials</p>
          <h2 class="rm-section-title">What clients say</h2>
        </div>
        <div class="bento-grid">
          <div class="bento-card bento-4 bento-testimonial">
            <span class="testimonial-stars">${stars(5)}</span>
            <p class="testimonial-text">"Caden transformed our MVP into a product users actually want to pay for. Revenue up 300% in 2 months."</p>
            <div class="author-row">
              <img class="author-avatar" src="https://api.dicebear.com/9.x/avataaars/svg?seed=SarahChen&backgroundColor=b6e3f4" alt="Sarah Chen" />
              <div>
                <div class="author-name">Sarah Chen</div>
                <div class="author-title">CEO, TechStart</div>
              </div>
            </div>
          </div>
          <div class="bento-card bento-4 bento-testimonial">
            <span class="testimonial-stars">${stars(5)}</span>
            <p class="testimonial-text">"The fastest designer I've ever worked with. Beautiful work, on time, every time. Feels like having a co-founder."</p>
            <div class="author-row">
              <img class="author-avatar" src="https://api.dicebear.com/9.x/avataaars/svg?seed=MarcusRodriguez&backgroundColor=ffd5dc" alt="Marcus Rodriguez" />
              <div>
                <div class="author-name">Marcus Rodriguez</div>
                <div class="author-title">Founder, GrowthLab</div>
              </div>
            </div>
          </div>
          <div class="bento-card bento-4 bento-testimonial">
            <span class="testimonial-stars">${stars(5)}</span>
            <p class="testimonial-text">"Our conversion rate doubled after the redesign. The attention to detail is unmatched. Worth every penny."</p>
            <div class="author-row">
              <img class="author-avatar" src="https://api.dicebear.com/9.x/avataaars/svg?seed=EmilyWatson&backgroundColor=d1f4d1" alt="Emily Watson" />
              <div>
                <div class="author-name">Emily Watson</div>
                <div class="author-title">VP Product, ScaleApp</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Bento -->
    <section class="rm-bento rm-bento--secondary">
      <div class="rm-container">
        <div class="rm-bento-header">
          <p class="rm-eyebrow">FAQ</p>
          <h2 class="rm-section-title">Common questions</h2>
        </div>
        <div class="bento-grid">
          <div class="bento-card bento-6 bento-faq">
            <h3>How long does a typical project take?</h3>
            <p>Most projects ship in 2–4 weeks depending on scope. Simple landing pages can be done in days. Complex apps take longer.</p>
          </div>
          <div class="bento-card bento-6 bento-faq">
            <h3>What's your pricing structure?</h3>
            <p>A flat monthly subscription with unlimited requests, or project-based pricing for one-off engagements.</p>
          </div>
          <div class="bento-card bento-6 bento-faq">
            <h3>Do you work with startups?</h3>
            <p>Absolutely. Whether you're pre-seed or Series A, if you're building something great, let's talk.</p>
          </div>
          <div class="bento-card bento-6 bento-faq">
            <h3>Can you help with ongoing work?</h3>
            <p>Yes. The monthly subscription is built for teams that need consistent design and development support.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA Bento -->
    <section class="rm-bento">
      <div class="rm-container">
        <div class="bento-grid">
          <div class="bento-card bento-12 bento-card--dark bento-final-cta">
            <p class="rm-eyebrow">Let's Build</p>
            <h2>Ready to ship your best product yet?</h2>
            <p>Stop settling for mediocre. Let's build something users love.</p>
            <div class="cta-row">
              <a href="https://cal.com/cadenburleson" target="_blank" rel="noopener noreferrer" class="rm-btn rm-btn-primary">
                Start Your Project
                <span class="btn-icon">${icon('arrow-right', { size: 16 })}</span>
              </a>
              <a href="/projects" class="rm-btn rm-btn-secondary">
                View Case Studies
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    ${renderFooter({ social: true })}
  `;

  // Typewriter animation for search prompts
  const textEl = container.querySelector('.search-prompt-text');
  if (textEl) {
    const phrases = [
      'Build a CMS that runs with a single line of code',
      'Turn my CSV into a structured database',
      'Design and ship a mobile app in two weeks',
      'Build an admin dashboard for my SaaS product',
      'Add AI features to our existing product',
      'Create a design system for our engineering team',
      'Migrate our backend to a modern tech stack'
    ];
    let phraseIdx = 0, charIdx = 0, isDeleting = false;
    function tick() {
      const phrase = phrases[phraseIdx];
      if (!isDeleting) {
        charIdx++;
        textEl.textContent = phrase.slice(0, charIdx);
        if (charIdx === phrase.length) {
          setTimeout(() => { isDeleting = true; tick(); }, 1800);
          return;
        }
        setTimeout(tick, 36);
      } else {
        charIdx--;
        textEl.textContent = phrase.slice(0, charIdx);
        if (charIdx === 0) {
          isDeleting = false;
          phraseIdx = (phraseIdx + 1) % phrases.length;
          setTimeout(tick, 400);
          return;
        }
        setTimeout(tick, 18);
      }
    }
    setTimeout(tick, 600);
  }

  // Prev/next for the work rail; touch and trackpad users can just swipe
  const rail = container.querySelector('.hero-work-rail');
  if (rail) {
    const navButtons = container.querySelectorAll('.hero-work-nav');
    const updateNav = () => {
      const max = rail.scrollWidth - rail.clientWidth - 2;
      navButtons.forEach(btn => {
        btn.disabled = btn.dataset.dir === '-1' ? rail.scrollLeft <= 2 : rail.scrollLeft >= max;
      });
    };
    navButtons.forEach(btn => btn.addEventListener('click', () => {
      const item = rail.querySelector('.hero-work-item');
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const step = item ? item.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      rail.scrollBy({ left: step * Number(btn.dataset.dir), behavior: smooth ? 'smooth' : 'auto' });
    }));
    rail.addEventListener('scroll', updateNav, { passive: true });
    updateNav();

    // Mouse drag to scroll. Touch and trackpads already scroll the rail natively.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let drag = null;
    let justDragged = false;
    let settleTimer = 0;

    const snapPositions = () => {
      const railLeft = rail.getBoundingClientRect().left;
      const padding = parseFloat(getComputedStyle(rail).scrollPaddingLeft) || 0;
      const max = rail.scrollWidth - rail.clientWidth;
      return [...rail.children].map(item =>
        Math.min(max, Math.max(0, item.getBoundingClientRect().left - railLeft + rail.scrollLeft - padding))
      );
    };

    // Snapping stays off until the glide lands exactly on a window, so it never jumps
    const settleOn = (target) => {
      const nearest = snapPositions().reduce((best, pos) =>
        Math.abs(pos - target) < Math.abs(best - target) ? pos : best, rail.scrollLeft);
      rail.scrollTo({ left: nearest, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      const release = () => {
        clearTimeout(settleTimer);
        rail.removeEventListener('scrollend', release);
        rail.classList.remove('is-free');
      };
      rail.addEventListener('scrollend', release);
      settleTimer = setTimeout(release, 700);
    };

    rail.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { startX: e.clientX, startScroll: rail.scrollLeft, lastX: e.clientX, lastT: e.timeStamp, velocity: 0, moved: false };
    });

    rail.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      if (!drag.moved) {
        if (Math.abs(dx) < 6) return;
        drag.moved = true;
        clearTimeout(settleTimer);
        rail.setPointerCapture(e.pointerId);
        rail.classList.add('is-dragging', 'is-free');
      }
      rail.scrollLeft = drag.startScroll - dx;
      const dt = e.timeStamp - drag.lastT;
      if (dt > 0) drag.velocity = (e.clientX - drag.lastX) / dt;
      drag.lastX = e.clientX;
      drag.lastT = e.timeStamp;
    });

    const endDrag = () => {
      if (!drag) return;
      const { moved, velocity } = drag;
      drag = null;
      if (!moved) return;
      rail.classList.remove('is-dragging');
      // A drag shouldn't also count as a click on the card under the pointer
      justDragged = true;
      setTimeout(() => { justDragged = false; }, 0);
      // Carry a flick forward before settling
      settleOn(rail.scrollLeft - velocity * 300);
    };
    rail.addEventListener('pointerup', endDrag);
    rail.addEventListener('pointercancel', endDrag);

    rail.addEventListener('click', (e) => {
      if (!justDragged) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    // Links and images would otherwise start a native drag and cancel ours
    rail.addEventListener('dragstart', (e) => e.preventDefault());
  }
}